/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

// No WeakMap to avoid strict function type issues.
type ReactivatedComponentType<TProps> = (
    props: TProps,
) => React.ReactElement<any, any> | null;

type QueryParams = string & {_brand: "queryParams"};

function urlToQueryParams(url: URL) {
    return url.search as QueryParams;
}

export function paramsToQueryString(params: Record<string, string | number>) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        searchParams.set(key, String(value));
    }
    return `?${searchParams.toString()}` as QueryParams;
}

export interface Locator {
    nominal: "reactivated.locator";
    name: string;
    tab: string;
    params: Record<string, string | number>;
    scenePath: string;
    resolved?: unknown;
    url: string;
    method: "push" | "replace";
    queryParams?: QueryParams;
}

export interface RouterLocation {
    query: URLSearchParams;
    pathname: string;
    locator: Locator;
}

export type ExtractRouteParams<T> = string extends T
    ? Record<string, string | number>
    : T extends `${infer _Start}<string:${infer Param}>/${infer Rest}`
      ? ExtractRouteParams<_Start> & {[k in Param]: string} & ExtractRouteParams<Rest>
      : T extends `${infer _Start}<number:${infer Param}>/${infer Rest}`
        ? ExtractRouteParams<_Start> & {[k in Param]: number} & ExtractRouteParams<Rest>
        : T extends `${infer _Start}<string:${infer Param}>` // eslint-disable-line unused-imports/no-unused-vars
          ? {[k in Param]: string}
          : T extends `${infer _Start}<number:${infer Param}>` // eslint-disable-line unused-imports/no-unused-vars
            ? {[k in Param]: number}
            : Record<never, never>;

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface Options {}

// https://stackoverflow.com/questions/56513652/extend-union-type
export type OptionsDefinition = Options[keyof Options];

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface Actions {}

export type ActionDefinition = Actions[keyof Actions];

export type ActionsDefinition = (ActionDefinition | false)[];

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface TabDefinition {}

type ExtractGuardedProps<TProps, TGuard> = TGuard extends (props: TProps) => undefined
    ? TProps
    : TGuard extends (props: TProps) => infer U | false
      ? U
      : unknown;

const REGEX = /<(string|number):([\w]*)>/g;

const processRoutePath = <TPath extends string, TTabs extends string[]>(
    path: TPath,
    tabs: [...TTabs],
) => {
    if (path.startsWith("/")) {
        throw new Error(`Paths should not start with a leading slash. Check ${path}`);
    }
    type Tokens = Record<
        Extract<keyof ExtractRouteParams<TPath>, string>,
        "string" | "number"
    >;
    const tokens = [...path.matchAll(REGEX)].map((token) => {
        return [token[2] as keyof Tokens, token[1] as "string" | "number"] as const;
    });
    const pattern = "/".concat(path.replace(/<(string|number)/g, "").replace(/>/g, ""));

    let regexPattern = pattern.replace(/:(\w+)/g, () => {
        return "([^\\/]+)";
    });

    if (tabs.length > 1) {
        const validTabs = tabs.slice(1).join("|");
        regexPattern = regexPattern.concat(`(?:$|(?<tab>${validTabs})/$)`);
    } else {
        regexPattern = regexPattern.concat("$");
    }

    const regex = new RegExp(`^(${regexPattern})`, "i");

    return {
        tokens,
        pattern,
        regex,
    };
};

export const interpolatePath = (
    pattern: string,
    params: Record<string, string | number>,
) => {
    return pattern.replace(
        /:(\w+)/g,
        (placeholderWithDelimiters, placeholderWithoutDelimiters: string) => {
            return params[placeholderWithoutDelimiters].toString();
        },
    );
};

export const matchRoutes = <TRoutes extends Array<AnyRouteDefinition>>(
    routes: [...TRoutes],
    path: string,
) => {
    for (const route of routes) {
        const match = path.match(route.definition.processed.regex);

        if (match != null) {
            const tab = match.groups?.tab ?? route.definition.tabs[0];
            const uncastedParams = match.slice(2);

            const params = Object.fromEntries<string | number>(
                route.definition.processed.tokens.map(
                    ([tokenName, tokenType], index) => {
                        const uncastedParam = uncastedParams[index];
                        if (tokenType === "number") {
                            return [tokenName, parseInt(uncastedParam)] as const;
                        }
                        return [tokenName, uncastedParam] as const;
                    },
                ),
            );

            return {route, params, tab};
        }
    }
    return null;
};

export function handleOnClickWithoutHijack(
    event: React.MouseEvent,
    handlers: {alwaysRuns?: () => unknown; callback: () => unknown},
) {
    // Only if the main mouse button without any modifiers is clicked do
    // we hijack the event.
    //
    // onClick always runs.
    handlers.alwaysRuns?.();

    if (
        event.button === 0 &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
    ) {
        event.preventDefault();
        handlers.callback();
    }
}

type Defined = number | boolean | symbol | bigint | object;

type SceneDependencies = Record<string, Defined | "ERROR">;

const sceneDependencies: SceneDependencies = {};

export const preloadSceneDependencies = (scenePath: string, data: unknown) => {
    sceneDependencies[scenePath] = data as Defined;
};

// Assigns stable IDs to hooks functions for use as React keys
// This allows keying by function identity rather than string representation
const hooksIdMap = new WeakMap<CallableFunction, string>();
let hooksIdCounter = 0;

const getHooksId = (hooks: CallableFunction) => {
    let id = hooksIdMap.get(hooks);
    if (id == null) {
        id = `hooks-${hooksIdCounter++}`;
        hooksIdMap.set(hooks, id);
    }
    return id;
};

interface RouterContext {
    isResolving: boolean;
}

const RouterContext = React.createContext<RouterContext>(undefined!);

const RouterProvider = (props: {children: React.ReactNode}) => {
    const [isResolving, setIsResolving] = React.useState(true);

    React.useEffect(() => {
        const handleResolved = () => setIsResolving(false);
        const handleResolving = () => setIsResolving(true);

        window.addEventListener("reactivated.resolved", handleResolved);
        window.addEventListener("reactivated.resolving", handleResolving);

        return () => {
            window.removeEventListener("reactivated.resolved", handleResolved);
            window.removeEventListener("reactivated.resolving", handleResolving);
        };
    }, []);

    return (
        <RouterContext.Provider value={{isResolving}}>
            {props.children}
        </RouterContext.Provider>
    );
};

export type RouterRenderPropsArgs = {
    locator: Locator;
    view: React.ReactNode;
    isResolving: boolean;
    tabs: Array<
        {
            locator: Locator | (() => void);
            isActive: boolean;
            hasErrors: boolean;
            isHidden?: boolean;
            icon: TabDefinition["icon"] | React.ComponentType<{className?: string}>;
        } & Omit<TabDefinition, "icon">
    >;
    actions: (ActionDefinition | false)[];
    parent: Locator | null;
    options: OptionsDefinition;
    reloadData: () => Promise<void>;
};

export type TransitionFn = (locator: Locator) => Promise<void>;

type RouterRenderProps = (props: RouterRenderPropsArgs) => React.ReactElement;

type ExtractProps<
    TRouteHooks,
    TPath,
    TResolved,
    TTabs extends string[],
> = TRouteHooks & {
    params: ExtractRouteParams<TPath>;
    resolved: TResolved;
    currentTab: TTabs[number];
    switchTab: (tab: TTabs[number]) => void;
    setResolved: (value: (prevState: TResolved) => Partial<TResolved>) => void;
    reloadData: () => Promise<void>;
    location: RouterLocation;
};

export type ConditionsTab = {
    view: React.ReactNode;
    title: string;
    hasErrors?: boolean;
    isHidden?: boolean;
    icon: TabDefinition["icon"] | React.ComponentType<{className?: string}>;
} & Omit<TabDefinition, "icon">;

export type Conditions<T, TTab extends string = string> = (props: T) =>
    | {
          view: React.ReactNode;
          options: OptionsDefinition;
          actions: ActionsDefinition;
      }
    | {
          tabs: ConditionsTab[];
          options: OptionsDefinition;
          actions: ActionsDefinition;
      }
    | {
          tabs: Record<TTab, ConditionsTab | null>;
          options: OptionsDefinition;
          actions: ActionsDefinition;
      };

type ConditionalImplementation<
    TRouteHooks,
    TPath,
    TResolved,
    TLocalHooks, // eslint-disable-line unused-imports/no-unused-vars
    TTabs extends string[],
    _TProps = ExtractProps<TRouteHooks, TPath, TResolved, TTabs>,
> = {
    type: "conditional";
    _routeHooksUsedOnlyForTyping: TRouteHooks;
    conditions: Conditions<_TProps>;
};

type RedirectImplementation<
    TRouteHooks,
    TPath,
    TResolved,
    TLocalHooks, // eslint-disable-line unused-imports/no-unused-vars
    TTabs extends string[],
    _TProps = ExtractProps<TRouteHooks, TPath, TResolved, TTabs>,
> = {
    type: "redirect";
    _routeHooksUsedOnlyForTyping: TRouteHooks;
    redirect: (props: _TProps) => Locator | Promise<Locator>;
};

export type StaticImplementation<
    TRouteHooks,
    TPath,
    TResolved,
    TLocalHooks,
    TTabs extends string[],
    _TProps = ExtractProps<TRouteHooks, TPath, TResolved, TTabs>,
> = {
    type: "static";
    withIndex: (component: ReactivatedComponentType<_TProps & TLocalHooks>) => {
        withActions: WithActions<
            TRouteHooks,
            TPath,
            TResolved,
            TLocalHooks,
            [TTabs[0]]
        >;
    };
    _routeHooksUsedOnlyForTyping: TRouteHooks;
    hooks: (props: _TProps) => TLocalHooks;
    tabs: {
        [K in TTabs[number]]: {
            component: ReactivatedComponentType<_TProps>;
            definition: {
                name: string;
                guard?: (props: any) => any;
                hasErrors?: (props: any) => boolean;
            };
            extra: TabDefinition;
        };
    };
    options: (props: _TProps) => OptionsDefinition;
    actions: (props: _TProps) => ActionsDefinition;
};

type RouteImplementation<
    TRouteHooks,
    TPath,
    TResolved,
    TLocalHooks,
    TTabs extends string[],
> =
    | StaticImplementation<TRouteHooks, TPath, TResolved, TLocalHooks, TTabs>
    | ConditionalImplementation<TRouteHooks, TPath, TResolved, TLocalHooks, TTabs>
    | RedirectImplementation<TRouteHooks, TPath, TResolved, TLocalHooks, TTabs>;

type WithActions<
    TRouteHooks,
    TPath,
    TResolved,
    TLocalHooks,
    TTabs extends string[],
    _TProps = ExtractProps<TRouteHooks, TPath, TResolved, TTabs> & TLocalHooks,
> = (actions: (props: _TProps) => ActionsDefinition) => {
    withOptions: (
        options: (props: _TProps) => OptionsDefinition,
    ) => StaticImplementation<TRouteHooks, TPath, TResolved, TLocalHooks, TTabs>;
};

type WithTab<
    TRouteHooks,
    TPath,
    TResolved,
    TLocalHooks,
    TTabs extends string[],
    TTabsSoFar extends string[],
    _TProps = ExtractProps<TRouteHooks, TPath, TResolved, TTabs> & TLocalHooks,
> = {
    <
        TTab extends TTabs[number],
        TGuard extends (props: _TProps) => unknown = (props: _TProps) => undefined,
    >(
        // definition: {name: TTab, guard: (props: TProps) => TGuardedProps, hasErrors?: {hack(props: TGuardedProps): boolean}["hack"]}}
        definition: {
            name: TTab;
            guard?: TGuard;
            hasErrors?: {
                hack(props: ExtractGuardedProps<_TProps, TGuard>): boolean;
            }["hack"];
        } & TabDefinition,
        component: ReactivatedComponentType<
            Omit<ExtractGuardedProps<_TProps, TGuard>, "currentTab"> & {
                currentTab: TTab;
            }
        >,
    ): {
        withTab: WithTab<
            TRouteHooks,
            TPath,
            TResolved,
            TLocalHooks,
            TTabs,
            [...TTabsSoFar, TTab]
        >;
        withActions: WithActions<
            TRouteHooks,
            TPath,
            TResolved,
            TLocalHooks,
            [...TTabsSoFar, TTab]
        >;
    };
};

type ExtractResolved<TName extends string, T> =
    Awaited<T> extends void ? unknown : {[K in TName]: Awaited<T>};

type Route<
    TName extends string,
    TPath extends string,
    TRouteHooks,
    TResolved,
    TTabs extends string[],
    _TProps = ExtractProps<TRouteHooks, TPath, TResolved, TTabs>,
> = {
    definition: {
        name: TName;
        path: TPath;
        resolve: {
            hack(
                params: ExtractRouteParams<TPath>,
                preloaded?: Partial<TResolved>,
            ): Promise<TResolved>;
        }["hack"];
        tabs: TTabs;
        hooks: (input?: unknown) => TRouteHooks | Locator;
        processed: ReturnType<typeof processRoutePath>;
    };
    withHooks: <TLocalHooks>(hooks: (props: _TProps) => TLocalHooks) => {
        withTab: WithTab<TRouteHooks, TPath, TResolved, TLocalHooks, TTabs, []>;
        withIndex: (component: ReactivatedComponentType<_TProps & TLocalHooks>) => {
            withActions: WithActions<
                TRouteHooks,
                TPath,
                TResolved,
                TLocalHooks,
                [TTabs[0]]
            >;
        };
    };
    useConditions: (
        conditions: Conditions<_TProps, TTabs[number]>,
    ) => ConditionalImplementation<TRouteHooks, TPath, TResolved, unknown, TTabs>;
    useRedirect: (
        redirect: (props: _TProps) => Locator | Promise<Locator>,
    ) => RedirectImplementation<TRouteHooks, TPath, TResolved, unknown, TTabs>;
    combine: <TRightPath extends string, TRightResolved>(
        right: Route<string, TRightPath, TRouteHooks, TRightResolved, TTabs>,
    ) => Route<
        string,
        TPath | TRightPath,
        TRouteHooks,
        TResolved | TRightResolved,
        TTabs
    >;
    parent?: {
        hack(params: ExtractRouteParams<TPath>, resolved: TResolved): Locator;
    }["hack"];
    reverse: (params: ExtractRouteParams<TPath>) => Locator;
    subroute: <
        TInnerName extends string,
        TInnerPath extends string,
        TInnerResolve,
        TInnerTabs extends string[] = ["index"],
    >(options: {
        name: TInnerName;
        path: TInnerPath;
        tabs?: [...TInnerTabs];
        resolve?: (props: {
            resolved: TResolved;
            params: ExtractRouteParams<`${TPath}${TInnerPath}`>;
        }) => TInnerResolve;
    }) => Route<
        TInnerName,
        `${TPath}${TInnerPath}`,
        TRouteHooks,
        ExtractResolved<TInnerName, TInnerResolve> & TResolved,
        TInnerTabs
    >;
};

type TabMap = {
    [x: string]: {
        component: ReactivatedComponentType<any>;
        definition: {name: string};
        extra: TabDefinition;
    };
};

const bindWithActions = <TProps, TLocalHooks>(
    hooks: (props: TProps) => TLocalHooks,
    tabs: TabMap,
) => {
    const withActions: WithActions<any, any, any, TLocalHooks, string[], any> = (
        actions,
    ) => {
        return {
            withOptions: (options) => ({
                type: "static",
                actions,
                options,
                hooks,
                tabs,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                _routeHooksUsedOnlyForTyping: null as any,

                withIndex: () => {
                    throw new Error(
                        "withIndex not implemented. This is just for typing.",
                    );
                },
            }),
        };
    };
    return withActions;
};

const bindWithTab = <TProps, TLocalHooks>(hooks: (props: TProps) => TLocalHooks) => {
    const tabs: TabMap = {};

    const withTab: WithTab<any, any, any, TLocalHooks, string[], any> = (
        definition,
        component,
    ) => {
        tabs[definition.name] = {definition, component, extra: definition};

        return {
            withTab,
            withActions: bindWithActions(hooks, tabs),
        };
    };

    return withTab;
};

// Helper types for routerFactory
// When TInput is never, hooks receives void; otherwise it receives input with close
type InputWithClose<TInput, TOutput> = [TInput] extends [never]
    ? void
    : TInput & {close: (result: TOutput | null) => void};

// Conditional return type: add markers only when TInput is not never
type RouteReturn<
    TInput,
    TOutput,
    TName extends string,
    TPath extends string,
    TRouteHooks,
    TResolved,
    TTabs extends string[],
> = [TInput] extends [never]
    ? Route<TName, TPath, TRouteHooks, ExtractResolved<TName, TResolved>, TTabs>
    : Route<TName, TPath, TRouteHooks, ExtractResolved<TName, TResolved>, TTabs> & {
          _inputType: TInput;
          _outputType: TOutput;
      };

// Standalone route builder - takes all dependencies as parameters
function bindRoute<TInput, TOutput, TRouteHooks>(
    resolversSoFar: Record<string, unknown>,
    hooks: (input: InputWithClose<TInput, TOutput>) => TRouteHooks | Locator,
) {
    return function route<
        TName extends string,
        TPath extends string,
        TResolved,
        TTabs extends string[] = ["index"],
        TResolvedSoFar = unknown,
    >(definition: {
        name: TName;
        path: TPath;
        resolve?: (props: {
            params: ExtractRouteParams<TPath>;
            resolved: TResolvedSoFar;
        }) => TResolved;
        tabs?: [...TTabs];
    }): RouteReturn<TInput, TOutput, TName, TPath, TRouteHooks, TResolved, TTabs> {
        type BoundRoute = Route<
            TName,
            TPath,
            TRouteHooks,
            ExtractResolved<TName, TResolved>,
            TTabs
        >;

        // Compute the props type for this route
        type Props = ExtractProps<
            TRouteHooks,
            TPath,
            ExtractResolved<TName, TResolved>,
            TTabs
        >;

        const tabs = definition.tabs ?? (["index"] as TTabs);
        const processed = processRoutePath(definition.path, tabs);
        const path = definition.path;
        const resolve = definition.resolve ?? (() => ({}) as TResolved);

        const resolvers = {
            ...resolversSoFar,
            [definition.name]: resolve,
        };

        const subroute: BoundRoute["subroute"] = (subDefinition) => {
            const innerRoute = bindRoute<TInput, TOutput, TRouteHooks>(
                resolvers,
                hooks,
            )({
                ...subDefinition,
                path: `${path}${subDefinition.path}`,
            });
            innerRoute.parent = (params, resolved) => {
                const scenePath = interpolatePath(processed.pattern, params);

                return {
                    nominal: "reactivated.locator",
                    name: definition.name,
                    tab: tabs[0],
                    params,
                    scenePath,
                    url: scenePath,
                    method: "push",
                    resolved,
                };
            };

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return innerRoute as any;
        };

        const baseRoute = {
            definition: {
                ...definition,
                processed,
                resolve: async (
                    params: ExtractRouteParams<TPath>,
                    preloaded?: Partial<ExtractResolved<TName, TResolved>>,
                ) => {
                    preloaded = preloaded ?? {};

                    const nextResolve: Record<string, unknown> = {};

                    for (const key of Object.keys(resolvers)) {
                        if (key in preloaded) {
                            const dependency = preloaded[key as keyof typeof preloaded];
                            nextResolve[key] = dependency;
                        } else {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
                            const dependency = (
                                resolvers as Record<string, CallableFunction>
                            )[key]({
                                params,
                                resolved: nextResolve,
                            });

                            if (
                                typeof dependency == "object" &&
                                dependency != null &&
                                "then" in dependency
                            ) {
                                window.dispatchEvent(
                                    new Event("reactivated.resolving"),
                                );
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                                dependency.catch((error: Error) =>
                                    console.error(`Error resolving ${key}`, error),
                                );
                            }
                            nextResolve[key] = await dependency;
                        }
                    }

                    window.dispatchEvent(new Event("reactivated.resolved"));

                    return nextResolve as ExtractResolved<TName, TResolved>;
                },
                tabs,
                hooks,
            },
            withHooks: <TLocalHooks,>(hooks: (props: Props) => TLocalHooks) => {
                return {
                    withIndex: (
                        component: ReactivatedComponentType<Props & TLocalHooks>,
                    ) => ({
                        withActions: bindWithActions(hooks, {
                            index: {
                                component,
                                definition: {name: "index"},
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                extra: null as any,
                            },
                        }),
                    }),
                    withTab: bindWithTab(hooks),
                };
            },
            useConditions: (conditions: Conditions<Props>) => {
                return {
                    type: "conditional",
                    conditions,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    _routeHooksUsedOnlyForTyping: null as any,
                };
            },
            useRedirect: (redirect: (props: Props) => Locator | Promise<Locator>) => {
                return {
                    type: "redirect",
                    redirect,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    _routeHooksUsedOnlyForTyping: null as any,
                };
            },
            combine: (right: AnyRouteDefinition) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return right as any;
            },
            reverse: (params: ExtractRouteParams<TPath>) => {
                const scenePath = interpolatePath(processed.pattern, params);

                return {
                    nominal: "reactivated.locator",
                    name: definition.name,
                    tab: tabs[0],
                    params,
                    scenePath,
                    url: scenePath,
                    method: "push",
                };
            },
            subroute,
        };

        // Add type markers only when TInput is not never (memory routers)
        // At runtime we always add them, but the return type controls visibility
        return {
            ...baseRoute,
            _inputType: null as unknown as TInput,
            _outputType: null as unknown as TOutput,
        } as RouteReturn<TInput, TOutput, TName, TPath, TRouteHooks, TResolved, TTabs>;
    };
}

// Unified router factory for both browser and memory routers
// - Browser routers: routerFactory()(() => hooks)
// - Memory routers: routerFactory<TInput, TOutput>()(({close, ...input}) => hooks)
export const routerFactory =
    <TInput = never, TOutput = never>() =>
    <TRouteHooks,>(
        hooks: (input: InputWithClose<TInput, TOutput>) => TRouteHooks | Locator,
    ) =>
        bindRoute<TInput, TOutput, TRouteHooks>({}, hooks);

type AnyRouteDefinition = Pick<
    Route<string, string, unknown, unknown, string[], unknown>,
    "definition" | "parent"
> & {_inputType?: unknown; _outputType?: unknown};

// Extract input/output types from a route
type ExtractRouteInput<T> = T extends {_inputType: infer I} ? I : never;
type ExtractRouteOutput<T> = T extends {_outputType: infer O} ? O : never;

type PathRewrite = (url: URL) => string;

export const createRouter = <
    TRoutes extends [AnyRouteDefinition, ...AnyRouteDefinition[]],
    // Infer TInput/TOutput from the first route's markers
    TInput = ExtractRouteInput<TRoutes[0]>,
    TOutput = ExtractRouteOutput<TRoutes[0]>,
>(
    routes: [...TRoutes],
    type: "browser" | "memory" = "browser",
    options?: {pathRewrite?: PathRewrite},
) => {
    const IS_IN_MEMORY = type == "memory";
    const pathRewrite = options?.pathRewrite;

    // Internal context for input/close - only used when Route receives input/close props
    type InputContextValue = TInput & {close: (result: TOutput | null) => void};
    const InputContext = React.createContext<InputContextValue>(null!);

    // Context for router-level hooks (shared across all routes, called once)
    type RouterHooksValue = ReturnType<TRoutes[0]["definition"]["hooks"]>;
    const RouterHooksContext = React.createContext<RouterHooksValue>(null!);

    type RouteNames = TRoutes[Extract<keyof TRoutes, number>]["definition"]["name"];
    type RouteImplementations = {
        [K in RouteNames]: RouteImplementation<
            Exclude<
                ReturnType<
                    Extract<
                        TRoutes[number],
                        {definition: {name: K}}
                    >["definition"]["hooks"]
                >,
                Locator
            >,
            Extract<TRoutes[number], {definition: {name: K}}>["definition"]["path"],
            Awaited<
                ReturnType<
                    Extract<
                        TRoutes[number],
                        {definition: {name: K}}
                    >["definition"]["resolve"]
                >
            >,
            unknown,
            Extract<TRoutes[number], {definition: {name: K}}>["definition"]["tabs"]
        >;
    };
    type StaticRouteImplementations = {
        [K in RouteNames]: StaticImplementation<
            Exclude<
                ReturnType<
                    Extract<
                        TRoutes[number],
                        {definition: {name: K}}
                    >["definition"]["hooks"]
                >,
                Locator
            >,
            Extract<TRoutes[number], {definition: {name: K}}>["definition"]["path"],
            Awaited<
                ReturnType<
                    Extract<
                        TRoutes[number],
                        {definition: {name: K}}
                    >["definition"]["resolve"]
                >
            >,
            unknown,
            Extract<TRoutes[number], {definition: {name: K}}>["definition"]["tabs"]
        >;
    };

    const getRouteDefinition = (name: string) => {
        const routeDefinition = routes.find((route) => route.definition.name == name);
        if (routeDefinition == null) {
            throw new Error(`Invalid route ${name}`);
        }
        return routeDefinition;
    };

    let _implementations: RouteImplementations | null = null;

    const getImplementations = () => {
        if (_implementations == null) {
            throw new Error("Routes are not mounted");
        }
        return _implementations;
    };

    const locator = <
        TName extends RouteNames,
        TParams extends ExtractRouteParams<
            Extract<TRoutes[number], {definition: {name: TName}}>["definition"]["path"]
        >,
        TTab extends Extract<
            TRoutes[number],
            {definition: {name: TName}}
        >["definition"]["tabs"][number],
        TResolve extends Partial<
            Awaited<
                ReturnType<
                    Extract<
                        TRoutes[number],
                        {definition: {name: TName}}
                    >["definition"]["resolve"]
                >
            >
        >,
    >(target: {
        route: `${TName}.${TTab}`;
        params: TParams;
        method?: "push" | "replace";
        resolved?: TResolve;
        queryParams?: QueryParams;
    }): Locator => {
        const [routeName, tabName] = target.route.split(".");
        const routeDefinition = getRouteDefinition(routeName);

        const scenePath = interpolatePath(
            routeDefinition.definition.processed.pattern,
            target.params,
        );
        const urlWithoutQueryParams =
            tabName == routeDefinition.definition.tabs[0]
                ? scenePath
                : `${scenePath}${tabName}/`;

        const url = `${urlWithoutQueryParams}${target.queryParams ?? ""}`;

        return {
            nominal: "reactivated.locator",
            scenePath,
            url,
            name: routeName,
            tab: tabName,
            params: target.params,
            resolved: target.resolved,
            method: target.method ?? "push",
            queryParams: target.queryParams,
        };
    };

    const getLocatorFromURL = (path: string, method: "push" | "replace" = "push") => {
        // Separate path from query string
        const url = new URL(
            path,
            import.meta.env.SSR ? "http://localhost" : window.location.origin,
        );
        const pathname = pathRewrite != null ? pathRewrite(url) : url.pathname;
        const queryParams = urlToQueryParams(url);

        const match = matchRoutes(routes, pathname);

        if (match == null) {
            return {locator: null, match: null};
        }

        const {route, params} = match;

        const scenePath = interpolatePath(route.definition.processed.pattern, params);
        const possibleTab = pathname.replace(scenePath, "").slice(0, -1);

        const tab = possibleTab === "" ? route.definition.tabs[0] : possibleTab;

        return {
            locator: locator({
                route: `${route.definition.name}.${tab}`,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                params: params as any,
                method,
                queryParams,
            }),
            match,
        };
    };

    const bindReloadData =
        (routeDefinition: AnyRouteDefinition, currentLocator: Locator) => async () => {
            try {
                const resolved = await routeDefinition.definition.resolve(
                    currentLocator.params,
                    currentLocator.resolved as Partial<unknown>,
                );
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                sceneDependencies[currentLocator.scenePath] = resolved as any;
            } catch (error: unknown) {
                if (error === "unauthorized") {
                    // TODO: hardcoded
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    void transition(getLocatorFromURL("/login/").locator!);
                } else {
                    throw error;
                }
            }
        };

    const bindSetResolved =
        (forceUpdate: () => void, currentLocator: Locator) =>
        (callback: (resolved: unknown) => unknown) => {
            const resolved = callback(
                sceneDependencies[currentLocator.scenePath] ?? {},
            );

            // If using pre-resolved dependencies, resolved won't be partial but
            // the current scene dependencies will be undefined. So we use a base
            // empty object.
            sceneDependencies[currentLocator.scenePath] = Object.assign(
                {},
                sceneDependencies[currentLocator.scenePath],
                resolved,
            );
            forceUpdate();
        };

    const transition = async (target: Locator): Promise<void> => {
        const {locator: source} = getLocatorFromURL(location.pathname);

        const routeDefinition = getRouteDefinition(target.name);

        const implementation =
            getImplementations()[routeDefinition.definition.name as RouteNames];

        if (
            source == null ||
            source.scenePath != target.scenePath ||
            target.method == "replace"
        ) {
            await bindReloadData(routeDefinition, target)();
        }

        if ("type" in implementation && implementation.type == "redirect") {
            const resolved = sceneDependencies[target.scenePath];

            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const redirect = await implementation.redirect({
                resolved,
                currentTab: target.tab,
            } as any);
            return transition(redirect);
        }

        if (IS_IN_MEMORY) {
            if (target.method == "replace") {
                history.replaceState(target.url, "");
            } else {
                history.pushState(target.url, "");
            }
            const navigationEvent = new PopStateEvent("reactivated.journey", {
                state: target.url,
            });
            window.dispatchEvent(navigationEvent);
        } else {
            if (target.method == "replace") {
                history.replaceState(null, "", target.url);
            } else {
                history.pushState(null, "", target.url);
            }
            const navigationEvent = new PopStateEvent("reactivated.navigate");
            window.dispatchEvent(navigationEvent);
        }

        // For when we want transition to return a promise and let us wait for it.
        /*
        let finishTransition = null;

        const handler = await new Promise<void>((resolve) => {
            finishTransition = () => resolve();
            window.addEventListener("reactivated.navigated", finishTransition);
        });

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (finishTransition != null) {
            window.removeEventListener("reactivated.navigated", finishTransition);
        }
        */
    };

    const back = () => {
        history.back();
    };

    const push = <
        TName extends RouteNames,
        TParams extends ExtractRouteParams<
            Extract<TRoutes[number], {definition: {name: TName}}>["definition"]["path"]
        >,
        TTab extends Extract<
            TRoutes[number],
            {definition: {name: TName}}
        >["definition"]["tabs"][number],
        TResolve extends Partial<
            Awaited<
                ReturnType<
                    Extract<
                        TRoutes[number],
                        {definition: {name: TName}}
                    >["definition"]["resolve"]
                >
            >
        >,
    >(
        routeNameAndTab: `${TName}.${TTab}`,
        params: TParams,
        resolved?: TResolve,
    ) => {
        void transition(
            locator({route: routeNameAndTab, params, resolved, method: "push"}),
        );
    };

    const replace: typeof push = (routeNameAndTab, params, resolved?) => {
        void transition(
            locator({route: routeNameAndTab, params, resolved, method: "replace"}),
        );
    };

    const unsafeTransitionByUrl = (url: string, method: "push" | "replace") => {
        const {locator} = getLocatorFromURL(url, method);

        if (locator != null) {
            void transition(locator);
        } else {
            console.warn(`Invalid navigation with URL "${url}"`);
        }
    };

    const reverse = <
        TName extends RouteNames,
        TParams extends ExtractRouteParams<
            Extract<TRoutes[number], {definition: {name: TName}}>["definition"]["path"]
        >,
        TTab extends Extract<
            TRoutes[number],
            {definition: {name: TName}}
        >["definition"]["tabs"][number],
    >(
        routeNameAndTab: `${TName}.${TTab}`,
        params: TParams,
    ) => {
        const [routeName, tabName] = routeNameAndTab.split(".");

        const routeDefinition = getRouteDefinition(routeName);

        const url = interpolatePath(
            routeDefinition.definition.processed.pattern,
            params,
        );
        if (tabName === routeDefinition.definition.tabs[0]) {
            return url;
        }
        return `${url}${tabName}/`;
    };

    const Link = <
        TName extends RouteNames,
        TParams extends ExtractRouteParams<
            Extract<TRoutes[number], {definition: {name: TName}}>["definition"]["path"]
        >,
        TTab extends Extract<
            TRoutes[number],
            {definition: {name: TName}}
        >["definition"]["tabs"][number],
        TResolve extends Partial<
            Awaited<
                ReturnType<
                    Extract<
                        TRoutes[number],
                        {definition: {name: TName}}
                    >["definition"]["resolve"]
                >
            >
        >,
    >(
        props:
            | {
                  locator: Locator;
                  url?: never;
                  method?: never;
                  route?: never;
                  params?: never;
                  resolved?: never;
                  children: React.ReactNode;
                  className?: string;
                  style?: React.CSSProperties;
                  onClick?: () => void;
              }
            | {
                  locator?: never;
                  url?: never;
                  method?: never;
                  route: `${TName}.${TTab}`;
                  params: TParams;
                  resolved?: TResolve;
                  children: React.ReactNode;
                  className?: string;
                  style?: React.CSSProperties;
                  onClick?: () => void;
              }
            | {
                  locator?: never;
                  url: string;
                  method?: "push" | "replace";
                  route?: never;
                  params?: never;
                  resolved?: never;
                  children: React.ReactNode;
                  className?: string;
                  style?: React.CSSProperties;
                  onClick?: () => void;
              },
    ) => {
        const target =
            props.locator != null
                ? props.locator
                : props.url != null
                  ? getLocatorFromURL(props.url, props.method).locator
                  : locator(props);

        if (target == null) {
            console.warn(`Invalid <Link> with URL "${props.url ?? ""}"`);
            return (
                <a className={props.className} href={props.url}>
                    {props.children}
                </a>
            );
        }

        const onClick: React.MouseEventHandler = (event) => {
            handleOnClickWithoutHijack(event, {
                alwaysRuns: props.onClick,
                callback: () => {
                    event.preventDefault();
                    void transition(target);
                },
            });
        };
        return (
            <a
                className={props.className}
                href={target.url}
                style={props.style}
                onClick={onClick}
            >
                {props.children}
            </a>
        );
    };

    // Separate component to isolate the guard boundary. When key changes,
    // this component unmounts/remounts entirely, resetting hook state.
    const GuardedContent = ({
        guardPassed,
        guardedProps,
        tabs,
        actions,
        isResolving,
        Component,
        currentLocator,
        parentLocator,
        options,
        reloadData,
        children,
    }: {
        guardPassed: boolean;
        guardedProps: Record<string, any>;
        tabs: RouterRenderPropsArgs["tabs"];
        actions: RouterRenderPropsArgs["actions"];
        isResolving: boolean;
        Component: ReactivatedComponentType<any>;
        currentLocator: Locator;
        parentLocator: Locator | null;
        options: OptionsDefinition;
        reloadData: () => Promise<void>;
        children: RouterRenderProps;
    }) => {
        if (!guardPassed) {
            return <div>Invalid</div>;
        }

        return (
            <>
                {children({
                    tabs,
                    actions,
                    isResolving,
                    view: <Component {...guardedProps} />,
                    locator: currentLocator,
                    parent: parentLocator,
                    options,
                    reloadData,
                })}
            </>
        );
    };

    const SceneHooks = ({
        routeHooks,
        currentLocator,
        implementation,
        route,
        setResolved,
        reloadData,
        children,
    }: {
        currentLocator: Locator;
        routeHooks: unknown;
        implementation: RouteImplementations[RouteNames];
        route: AnyRouteDefinition;
        setResolved: (resolved: (prevState: any) => any) => void;
        reloadData: () => Promise<void>;
        children: RouterRenderProps;
    }) => {
        const currentDependencies = sceneDependencies[currentLocator.scenePath];
        const switchTab = (tab: string) => {
            const nextTab = locator({
                route: `${currentLocator.name}.${tab}`,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                params: currentLocator.params as any,
            });
            void transition(nextTab);
        };
        const {isResolving} = React.useContext(RouterContext);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const params = currentLocator.params as any;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const resolved = currentDependencies as any;
        const currentTab = currentLocator.tab;
        const castedRouteHooks =
            routeHooks as typeof implementation._routeHooksUsedOnlyForTyping;
        const parentLocator = route.parent?.(currentLocator.params, resolved) ?? null;

        const unguardedProps = Object.assign({}, castedRouteHooks, {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            resolved,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            params,
            currentTab,
            switchTab,
            setResolved,
            reloadData,
            location: {
                query: new URLSearchParams(
                    import.meta.env.SSR
                        ? (currentLocator.queryParams ?? "")
                        : window.location.search,
                ),
                pathname: currentLocator.url,
                locator: currentLocator,
            },
        });

        if (implementation.type == "redirect") {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            React.useEffect(() => {
                void (async () => {
                    const redirect = await implementation.redirect(unguardedProps);

                    void transition(redirect);
                })();
            }, []);
            return <></>;
        } else if (implementation.type == "conditional") {
            const currentConditions = implementation.conditions(unguardedProps);

            // eslint-disable-next-line react-hooks/rules-of-hooks
            const [activeTab, setActiveTab] = React.useState(0);

            const {tabs, view} = (() => {
                if (!("tabs" in currentConditions)) {
                    return {tabs: [], view: currentConditions.view};
                }

                // Record form: keys are route tab names, backed by URL navigation
                // Array form: local state tabs, no URL changes
                if (!Array.isArray(currentConditions.tabs)) {
                    const entries = Object.entries(currentConditions.tabs).filter(
                        (entry): entry is [string, ConditionsTab] => entry[1] != null,
                    );
                    const activeEntry =
                        entries.find(([key]) => key === currentTab)?.[1] ??
                        entries[0][1];
                    return {
                        tabs: entries.map(([routeTab, tab]) => ({
                            ...tab,
                            locator: () => switchTab(routeTab),
                            hasErrors: tab.hasErrors ?? false,
                            isActive: currentTab === routeTab,
                        })),
                        view: activeEntry.view,
                    };
                }

                return {
                    tabs: currentConditions.tabs.map((tab, index) => ({
                        ...tab,
                        locator: () => setActiveTab(index),
                        hasErrors: tab.hasErrors ?? false,
                        isActive: index === activeTab,
                    })),
                    view: currentConditions.tabs[activeTab].view,
                };
            })();

            return (
                <>
                    {children({
                        tabs,
                        isResolving,
                        actions: currentConditions.actions.map((action) => action),
                        view,
                        locator: currentLocator,
                        parent: parentLocator,
                        options: currentConditions.options,
                        reloadData,
                    })}
                </>
            );
        }
        const sceneHooks = implementation.hooks(unguardedProps);

        const unguardedPropsWithHooks = Object.assign({}, unguardedProps, sceneHooks);

        const tabs = Object.values<(typeof implementation.tabs)[string]>(
            implementation.tabs,
        )
            .filter((tab) => {
                return tab.definition.guard?.(unguardedPropsWithHooks) != false;
            })
            .map((tab) => {
                const tabLocator = locator({
                    route: `${route.definition.name}.${tab.definition.name}`,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    params: currentLocator.params as any,
                });

                const hasErrors =
                    tab.definition.hasErrors?.(unguardedPropsWithHooks) ?? false;

                return {
                    ...tab.extra,
                    hasErrors,
                    locator:
                        IS_IN_MEMORY == true
                            ? () => transition(tabLocator)
                            : tabLocator,
                    url: tabLocator.url,
                    name: tab.definition.name,
                    isActive: tabLocator.url == currentLocator.url,
                };
            });

        const actions = implementation.actions(unguardedPropsWithHooks);
        const options = implementation.options(unguardedPropsWithHooks);
        const Component =
            implementation.tabs[currentTab as keyof typeof implementation.tabs]
                .component;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const guardedProps =
            implementation.tabs[
                currentTab as keyof typeof implementation.tabs
            ].definition.guard?.(unguardedPropsWithHooks) ?? unguardedPropsWithHooks;

        const guardPassed = guardedProps !== false;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        React.useEffect(() => {
            if (!guardPassed) {
                switchTab(route.definition.tabs[0]);
            }
        }, [guardPassed]);

        // Key the entire render based on guard state to force complete remount
        // when guard changes. This prevents hooks mismatch errors.
        return (
            <GuardedContent
                key={guardPassed ? "guard-passed" : "guard-failed"}
                guardPassed={guardPassed}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                guardedProps={guardedProps}
                tabs={tabs}
                actions={actions}
                isResolving={isResolving}
                Component={Component}
                currentLocator={currentLocator}
                parentLocator={parentLocator}
                options={options}
                reloadData={reloadData}
            >
                {children}
            </GuardedContent>
        );
    };

    const RouteHooks = ({
        currentLocator,
        implementation,
        route,
        setResolved,
        reloadData,
        children,
    }: {
        currentLocator: Locator;
        implementation: RouteImplementations[RouteNames];
        route: AnyRouteDefinition;
        setResolved: (resolved: (prevState: any) => any) => void;
        reloadData: () => Promise<void>;
        children: RouterRenderProps;
    }) => {
        // Read router-level hooks from context (called once in Route, shared across all routes)
        const routeHooks = React.useContext(RouterHooksContext);

        return (
            <SceneHooks
                routeHooks={routeHooks}
                currentLocator={currentLocator}
                setResolved={setResolved}
                reloadData={reloadData}
                implementation={implementation}
                route={route}
            >
                {children}
            </SceneHooks>
        );
    };

    // Context for scene-specific data (reloadData, setResolved) that changes per scene
    type SceneDataContextValue = {
        reloadData: () => Promise<void>;
        setResolved: (resolved: (prevState: any) => any) => void;
        currentLocator: Locator;
        implementation: RouteImplementations[RouteNames];
        route: AnyRouteDefinition;
    };
    const SceneDataContext = React.createContext<SceneDataContextValue>(null!);

    // Component that calls hooks and is keyed by the hooks function identity
    // This ensures React sees consistent hooks when switching between routes with different hooks
    // IMPORTANT: This is OUTSIDE DataLoader so hooks persist across scene changes within the same chain
    const RouterHooksProvider = ({
        route,
        inputWithClose,
        children,
    }: {
        route: AnyRouteDefinition;
        inputWithClose: InputContextValue | undefined;
        children: React.ReactNode;
    }) => {
        const routerHooks = route.definition.hooks(inputWithClose);

        // Check if hooks returned a redirect
        if (
            typeof routerHooks == "object" &&
            routerHooks != null &&
            "nominal" in routerHooks &&
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            (routerHooks as Locator).nominal === "reactivated.locator"
        ) {
            const locator: Locator = routerHooks as Locator;
            // eslint-disable-next-line react-hooks/rules-of-hooks
            React.useEffect(() => {
                void transition(locator);
            }, []);
            return <></>;
        }

        return (
            <RouterHooksContext.Provider value={routerHooks as RouterHooksValue}>
                {children}
            </RouterHooksContext.Provider>
        );
    };

    // Renders RouteHooks using both router hooks (from context) and scene data (from context)
    // Keyed by scenePath so it remounts when switching scenes (different scene hooks)
    const SceneRenderer = ({children}: {children: RouterRenderProps}) => {
        const {reloadData, setResolved, currentLocator, implementation, route} =
            React.useContext(SceneDataContext);

        return (
            <RouteHooks
                key={currentLocator.scenePath}
                reloadData={reloadData}
                setResolved={setResolved}
                currentLocator={currentLocator}
                implementation={implementation}
                route={route}
            >
                {children}
            </RouteHooks>
        );
    };

    // Loads data via resolve and provides scene data context
    // NOT keyed by scenePath - instead reacts to scenePath changes via useEffect
    // This allows RouterHooksProvider (child) to persist across scene changes
    const DataLoader = ({
        currentLocator,
        implementation,
        route,
        children,
    }: {
        currentLocator: Locator;
        implementation: RouteImplementations[RouteNames];
        route: AnyRouteDefinition;
        children: React.ReactNode;
    }) => {
        const [, forceUpdate] = React.useState(0);
        const {isResolving} = React.useContext(RouterContext);
        const currentDependencies = sceneDependencies[currentLocator.scenePath];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const isLoaded = currentDependencies != null;

        const reloadData = bindReloadData(route, currentLocator);

        const setResolved = bindSetResolved(
            () => forceUpdate((counter) => counter + 1),
            currentLocator,
        );

        // Load data when scenePath changes (including initial load)
        React.useEffect(() => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (sceneDependencies[currentLocator.scenePath] == null) {
                void reloadData();
            }
        }, [currentLocator.scenePath]);

        // Show loading until resolve completes
        if (isLoaded == false && isResolving === true) {
            return <div className="text-center">Loading</div>;
        }

        return (
            <SceneDataContext.Provider
                value={{reloadData, setResolved, currentLocator, implementation, route}}
            >
                {children}
            </SceneDataContext.Provider>
        );
    };

    // Props type depends on whether TInput/TOutput are specified
    type RouteProps = [TInput] extends [never]
        ? {
              pathname?: string;
              children: RouterRenderProps;
          }
        : {
              pathname?: string;
              children: RouterRenderProps;
              input: TInput;
              close: (result: TOutput | null) => void;
          };

    const Route = (props: RouteProps) => {
        const {pathname, children} = props;
        const input = "input" in props ? props.input : undefined;
        const close = "close" in props ? props.close : undefined;

        const [currentPath, setCurrentPath] = React.useState(
            pathname ?? location.pathname + location.search,
        );

        const eventName = IS_IN_MEMORY ? "reactivated.journey" : "reactivated.navigate";
        React.useEffect(() => {
            const onLocationChange = (event: Event) => {
                if (event instanceof PopStateEvent) {
                    if (IS_IN_MEMORY == true) {
                        setCurrentPath(
                            (event.state as string | null) ?? pathname ?? "",
                        );
                    } else {
                        setCurrentPath(location.pathname + location.search);
                    }
                }
            };
            window.addEventListener(eventName, onLocationChange);
            window.addEventListener("popstate", onLocationChange);

            return () => {
                window.removeEventListener(eventName, onLocationChange);
                window.removeEventListener("popstate", onLocationChange);
            };
        }, []);

        const {locator: currentLocator, match} = getLocatorFromURL(currentPath);

        if (currentLocator == null) {
            return null;
        }
        const implementation =
            getImplementations()[match.route.definition.name as RouteNames];

        // Structure: RouterProvider → DataLoader (resolve first) → RouterHooksProvider (hooks after)
        // - DataLoader is NOT keyed - reacts to scenePath changes via useEffect
        // - RouterHooksProvider is keyed by hooks function (persists across scenes in same chain)
        // This ensures:
        // 1. Bootstrap/auth data is loaded before hooks check auth state
        // 2. Hooks persist when navigating within the same chain
        const inputWithClose =
            input != null && close != null ? {...input, close} : undefined;

        const content = (
            <RouterProvider>
                <DataLoader
                    currentLocator={currentLocator}
                    implementation={implementation}
                    route={match.route}
                >
                    <RouterHooksProvider
                        key={getHooksId(match.route.definition.hooks)}
                        route={match.route}
                        inputWithClose={inputWithClose}
                    >
                        <SceneRenderer>{children}</SceneRenderer>
                    </RouterHooksProvider>
                </DataLoader>
            </RouterProvider>
        );

        // Wrap with InputContext.Provider when input/close are provided
        if (input != null && close != null) {
            return (
                <InputContext.Provider value={{...input, close}}>
                    {content}
                </InputContext.Provider>
            );
        }

        return content;
    };

    const mount = (implementations: RouteImplementations) => {
        _implementations = implementations;
        return {Route};
    };

    // Extract tabs from the first route
    type FirstRouteTabs = TRoutes[0]["definition"]["tabs"][number];

    return {
        _input: null as unknown as TInput,
        _output: null as unknown as TOutput,
        _implementations: null as unknown as RouteImplementations,
        _tabs: null as unknown as FirstRouteTabs,
        _routes: routes,
        _testing: null as any as StaticRouteImplementations,
        mount,
        back,
        reverse,
        push,
        replace,
        unsafeTransitionByUrl,
        locator,
        transition,
        Link,
        getLocatorFromURL,
    };
};
